import clsx from 'clsx';
import { RefObject } from 'react';
import { useController } from 'react-hook-form';
import { IoMdArrowDropdown, IoMdArrowDropup } from 'react-icons/io';

interface Props {
  name: string;
  type: 'hour' | 'minute';
  inputRef?: RefObject<HTMLInputElement>;
  nextInputRef?: RefObject<HTMLInputElement>;
  isInvalid?: boolean;
  onChangeCallback?: () => void;
}

export const TimeFilterInputField = ({
  name,
  type,
  inputRef,
  nextInputRef,
  isInvalid = false,
  onChangeCallback,
}: Props) => {
  const { field, fieldState } = useController({
    name: `${name}.${type}`,
  });
  const { value, onChange } = field;

  const maxValue = type === 'hour' ? 23 : 59;

  return (
    <div className="flex flex-col items-center">
      <button
        type="button"
        tabIndex={-1}
        onClick={() => {
          const newValue = parseInt(value || '00', 10) - 1;
          const formattedValue = newValue < 0
            ? maxValue.toString()
            : newValue.toLocaleString('en-US', {
              minimumIntegerDigits: 2,
              useGrouping: false,
            });
          onChange(formattedValue);
          setTimeout(() => {
            onChangeCallback?.();
          }, 100);
        }}
        className="flex w-full justify-center py-[4px]"
      >
        <IoMdArrowDropup className="size-[24px] text-text-primary" />
      </button>
      <input
        ref={inputRef}
        className={clsx(
          'h-[70px] w-full rounded-[6px] border bg-white px-[16px] text-center text-[36px] focus:bg-brand-primary focus:text-white focus:outline-none focus:!ring-0',
          fieldState.error || isInvalid
            ? 'border-brand-attention focus:border-brand-attention'
            : 'border-border-brand focus:border-border-brand',
        )}
        value={value}
        onBlur={(e) => {
          const currentValue = parseInt(e.currentTarget.value, 10);
          if (currentValue < 10) {
            const formattedValue = currentValue.toLocaleString('en-US', {
              minimumIntegerDigits: 2,
              useGrouping: false,
            });
            onChange(formattedValue);
            setTimeout(() => {
              onChangeCallback?.();
            }, 100);
          }
        }}
        onFocus={(e) => e.currentTarget.select()}
        onChange={(e) => {
          const validInput = e.currentTarget.value.replace(/[^\d]+/g, '');
          if (validInput.length > 2) {
            return;
          }
          if (parseInt(validInput, 10) > maxValue) {
            onChange('00');
            setTimeout(() => {
              onChangeCallback?.();
            }, 1);
          } else {
            onChange(validInput);
            setTimeout(() => {
              onChangeCallback?.();
            }, 100);
          }
          if (validInput.length === 2 && nextInputRef?.current) {
            nextInputRef.current.focus();
          }
        }}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => {
          const newValue = parseInt(value || '00', 10) + 1;
          const formattedValue = newValue > maxValue
            ? '00'
            : newValue.toLocaleString('en-US', {
              minimumIntegerDigits: 2,
              useGrouping: false,
            });
          onChange(formattedValue);
          setTimeout(() => {
            onChangeCallback?.();
          }, 100);
        }}
        className="flex w-full justify-center py-[4px]"
      >
        <IoMdArrowDropdown className="size-[24px] text-text-primary" />
      </button>
    </div>
  );
};
